import { useCallback, useEffect, useRef, useState } from 'react';

import type { FetchPage } from '../hub-pages';
import { HUB_PAGE_SIZE } from '../list-queries';

/**
 * "Show more" over a list whose first page is already on screen (the hubs'
 * build-time seed). Appends `pageSize` rows per press, fetched by offset after
 * `initial`. Rows already shown are skipped by id — a safety net for a
 * catalogue that changed between the build and the press, which offsets alone
 * can't see.
 *
 * `hasMore` is learnt by asking for one row beyond the page; before the first
 * press, a full first page is taken to mean there may be more — unless the
 * caller already knows (`initialHasMore`: a filtered hub fetches its first
 * page with that extra row too).
 */
export function useLoadMore<T extends { id: string }>(
  initial: T[],
  fetchPage: FetchPage<T>,
  pageSize: number = HUB_PAGE_SIZE,
  initialHasMore: boolean = true,
) {
  const [extra, setExtra] = useState<T[]>([]);
  // Rows the server has returned past `initial` — the next offset. Counted
  // before de-duplication, since offsets index the server's list.
  const [fetched, setFetched] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Bumped with every new first page, so a press still in flight for the old
  // one (a hub whose filters just changed) can't append its rows to the new.
  const generation = useRef(0);

  // A new first page (the unseeded fallback's client fetch landing, a
  // refetch, or a filter change) restarts paging from it.
  useEffect(() => {
    generation.current += 1;
    setExtra([]);
    setFetched(0);
    setHasMore(true);
    setError(false);
  }, [initial]);

  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const loadMore = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(false);
    const gen = generation.current;
    try {
      const rows = await fetchPage(initial.length + fetched, pageSize + 1);
      if (!mounted.current || gen !== generation.current) return;
      const page = rows.slice(0, pageSize);
      setExtra((prev) => {
        const seen = new Set([...initial, ...prev].map((row) => row.id));
        return [...prev, ...page.filter((row) => !seen.has(row.id))];
      });
      setFetched((n) => n + page.length);
      setHasMore(rows.length > pageSize);
    } catch {
      if (mounted.current && gen === generation.current) setError(true);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [loading, fetchPage, initial, fetched, pageSize]);

  return {
    /** Rows appended after `initial`. */
    extra,
    /** Whether to show the button: a full first page, and no short page since. */
    canLoadMore: hasMore && initialHasMore && initial.length >= pageSize,
    loading,
    error,
    loadMore,
  };
}
